import axios from "axios";
import { sendMessageWithNcSignature, sendMessageWithMpcWallet, baseUrl, apiVersion, uploadFileWithMpcWallet, } from "./dwcommonutils";
import { createEmptyLogsAndVars, dwReportError, recordPotentialAxiosError, recordVariable, } from "../diagnostics/clientErrors";
import { jwtMessageAsGetReturnsJson, jwtNoParamReturnsBinary, jwtUploadFileReturnsJson, } from "./jwtcomm";
import { decryptBlob, encryptFile } from "../dataEncryption";
// (OK?) MPC Wallet Management Service / 1. Create Client
export const createMpcClient = async (provider, walletAddress, overrideApiVersion) => {
    const myApiVersion = overrideApiVersion ?? apiVersion;
    const postObject = {
        method: "post",
        endpoint: `/api/${myApiVersion}/mpc/client`,
        ncwa: walletAddress,
    };
    return sendMessageWithNcSignature(provider, walletAddress, postObject);
};
export const getClientInfo = async (provider, walletAddress, mpcClientId) => {
    const postObject = {
        method: "get",
        endpoint: `/api/${apiVersion}/mpc/client`,
        clientId: mpcClientId,
    };
    return sendMessageWithNcSignature(provider, walletAddress, postObject);
};
// MPC Wallet Management Service / 3. Refresh PortalHQ Client Session Token Using Non-Custodial Wallet Signature
export const refreshClientSessionToken = async (provider, walletAddress, mpcClientId, overrideApiVersion) => {
    const myApiVersion = overrideApiVersion ?? apiVersion;
    const postObject = {
        method: "post",
        endpoint: `/api/${myApiVersion}/mpc/refresh`,
        ncwa: walletAddress,
        clientId: mpcClientId,
    };
    return sendMessageWithNcSignature(provider, walletAddress, postObject);
};
// MPC Wallet Management Service / 4. Refresh PortalHQ Client Session Token Using the Recovery Authorization Token
export const refreshClientSessionTokenByRecoveryToken = async (recoveryAuthToken, mpcClientId) => {
    const messageObject = {
        clientId: mpcClientId,
    };
    const result = await jwtMessageAsGetReturnsJson("post", `/api/${apiVersion}/mpc/refresh-using-token`, recoveryAuthToken, messageObject);
    return result;
};
export const getAvatar = async (portalClientId, clientSessionToken, userShare) => {
    const sendObject = {
        method: "get",
        endpoint: `/api/${apiVersion}/users/avatar`,
        clientId: portalClientId,
    };
    return await sendMessageWithMpcWallet(clientSessionToken, userShare, sendObject, "blob");
};
export const setAvatar = async (portalClientId, clientSessionToken, userShare, file) => {
    const sendObject = {
        method: "post",
        endpoint: `/api/${apiVersion}/users/avatar`,
        clientId: portalClientId,
    };
    await uploadFileWithMpcWallet(clientSessionToken, userShare, sendObject, file);
};
export const getAvatarJwt = async (provider, authToken) => {
    try {
        const responseBlob = await jwtNoParamReturnsBinary("get", `/api/${apiVersion}/users/avatar`, authToken);
        if (responseBlob.size === 0) {
            return undefined;
        }
        const decryptedBlob = await decryptBlob(provider, responseBlob);
        return new File([decryptedBlob], "avatar");
    }
    catch (error) {
        console.log(error);
        const errorText = await error?.cause?.response?.data?.text();
        if (errorText !== undefined && errorText.includes("object not found")) {
            return undefined;
        }
        throw error;
    }
};
export const setAvatarJwt = async (provider, authToken, file) => {
    const encryptedFile = await encryptFile(provider, file);
    await jwtUploadFileReturnsJson("post", `/api/${apiVersion}/users/avatar`, authToken, encryptedFile);
};
export const getUserProfileJwt = async (provider, authToken) => {
    try {
        const responseBlob = await jwtNoParamReturnsBinary("get", `/api/${apiVersion}/users/profile`, authToken);
        if (responseBlob.size === 0) {
            return createDefaultUserProfile();
        }
        const decryptedBlob = await decryptBlob(provider, responseBlob);
        const responseText = await new Response(decryptedBlob).text();
        return JSON.parse(responseText);
    }
    catch (error) {
        //const errorText = await error?.cause?.response?.data?.text();
        console.log(error);
        if (error?.cause?.response?.status === 401 ||
            error?.cause?.cause?.response?.status === 401 ||
            error?.cause?.response?.status === 500 ||
            error?.cause?.cause?.response?.status === 500) {
            return createDefaultUserProfile();
        }
        throw error;
    }
};
export const myRemoveUserInfoJwt = async (authToken) => {
    const sendObject = {};
    return await jwtMessageAsGetReturnsJson("delete", `/api/${apiVersion}/users/remove`, authToken, sendObject);
};
function createDefaultUserProfile() {
    return {
        name: "",
        email: "",
        twoFactor: false,
        emailNotification: false,
        profileVisibility: false,
        dataSharing: false,
        darkMode: true,
        loginNotifications: true,
        showBalance: false,
    };
}
export const setUserProfileJwt = async (provider, authToken, userProfile) => {
    const userProfileFile = new File([JSON.stringify(userProfile)], "user_profile.json", {
        type: "text/json",
    });
    const encryptedUserProfileFile = await encryptFile(provider, userProfileFile);
    return await jwtUploadFileReturnsJson("post", `/api/${apiVersion}/users/profile`, authToken, encryptedUserProfileFile);
};
export const notifyMpcWalletOnUserSharesStored = async (clientSessionToken, secp256k1ShareId, ed25519ShareId) => {
    const response = await axios.patch(`${baseUrl}/api/${apiVersion}/mpc/signing-share-pairs`, {
        status: "STORED_CLIENT",
        signingSharePairIds: [secp256k1ShareId, ed25519ShareId],
    }, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${clientSessionToken}`,
        },
    });
    return response.data;
};
// (OK?) MPC Wallet Management System / 6. Update MPC Wallet Backup Share Pairs Status
export const notifyMpcWalletOnBackupSharesStored = async (clientSessionToken, secp256k1ShareId, ed25519ShareId, overrideApiVersion) => {
    const myApiVersion = overrideApiVersion ?? apiVersion;
    const response = await axios.patch(`${baseUrl}/api/${myApiVersion}/mpc/backup-share-pairs`, {
        status: "STORED_CLIENT_BACKUP_SHARE",
        backupSharePairIds: [secp256k1ShareId, ed25519ShareId],
    }, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${clientSessionToken}`,
        },
    });
    //console.log("/mpc/backup-share-pairs returns: ", response);
    return response.data;
};
// (OK?) MPC Wallet Management Service / 2. Is Client Session Token Valid
export const isClientSessionTokenValid = async (clientSessionToken, overrideApiVersion) => {
    const logsAndVars = createEmptyLogsAndVars("dwclientutils:isClientSessionTokenValid");
    try {
        const baseUrl = process.env.DWINITY_BACKEND ?? process.env.DWINITY_BACKEND ?? "https://api.dev.dwinity.com";
        const myApiVersion = overrideApiVersion ?? apiVersion;
        const url = `${baseUrl}/api/${myApiVersion}/mpc/is-session-token-valid`;
        recordVariable(logsAndVars, "url", url);
        recordVariable(logsAndVars, "clientSessionToken (query param)", clientSessionToken);
        const response = await axios.get(url, {
            params: {
                clientSessionToken: clientSessionToken,
            },
            headers: {
                "Content-Type": "application/json",
            },
        });
        recordVariable(logsAndVars, "response", response);
        return response.data.isValid;
    }
    catch (error) {
        await recordPotentialAxiosError(logsAndVars, error);
        dwReportError(null, undefined, error, { logsAndVars });
        return false;
        //throw new Error(error.message);
    }
};
